/* Reya · Concierge — on-site shopping assistant.
   Answers from the FAQ in theme settings, guides by meaning / gift recipient / budget, searches the live catalogue
   (Shopify predictive search + collection JSON) and adds to bag from the chat. No data leaves the store. */
(() => {
  if (window.__rrConcierge) return;
  window.__rrConcierge = true;

  const cfgEl = document.getElementById('RrChatConfig');
  const openBtn = document.querySelector('[data-rr-chat-open]');
  if (!cfgEl || !openBtn) return;
  let C;
  try {
    C = JSON.parse(cfgEl.textContent);
  } catch (e) {
    return;
  }
  const root = C.root || '/';
  const KEY = 'rr:chat';
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- helpers ---------- */
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  // [label](url) → safe link (relative or https only)
  const rich = (s) =>
    esc(s).replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (m, label, url) =>
      /^(\/|https:\/\/)/.test(url) ? `<a href="${url}">${label}</a>` : label
    );
  const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9₹\s]/g, ' ').replace(/\s+/g, ' ').trim();
  const lines = (s) => String(s || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const cols = (l) => l.split('|').map((x) => x.trim());
  const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'INR';
  const money = (amount) => {
    const n = Number(amount) || 0;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: cur, maximumFractionDigits: n % 1 ? 2 : 0 }).format(n);
    } catch (e) {
      return n.toFixed(2);
    }
  };
  const img = (src, w) => {
    if (!src) return '';
    const u = src.startsWith('//') ? `https:${src}` : src;
    return u + (u.includes('?') ? '&' : '?') + `width=${w}`;
  };
  const wait = (ms) => new Promise((r) => setTimeout(r, reduce ? 0 : ms));

  const chips = lines(C.chips).map((l) => { const [label, q] = cols(l); return { label, q: q || label }; });
  const faq = lines(C.faq).map((l) => {
    const [q, keys, ...rest] = cols(l);
    return { q, keys: (keys || '').split(',').map((k) => norm(k)).filter(Boolean), a: rest.join(' | ') };
  });
  const meanings = lines(C.meanings).map((l) => {
    const [label, handle, words, intro] = cols(l);
    return { label, handle, words: (words || '').split(',').map(norm).filter(Boolean), intro };
  });
  const gifts = lines(C.gifts).map((l) => { const [label, handle] = cols(l); return { label, handle }; });

  /* ---------- catalogue ---------- */
  const collectionProducts = async (handle, limit = 8) => {
    try {
      const r = await fetch(`${root}collections/${encodeURIComponent(handle)}/products.json?limit=${limit}`);
      if (!r.ok) return [];
      const j = await r.json();
      return (j.products || []).map((p) => {
        const v = p.variants.find((x) => x.available) || p.variants[0];
        return { title: p.title, url: `${root}products/${p.handle}`, handle: p.handle, price: Math.min(...p.variants.map((x) => Number(x.price))), image: p.images[0] && p.images[0].src, variant: v && v.id, available: p.variants.some((x) => x.available) };
      });
    } catch (e) {
      return [];
    }
  };
  const search = async (q) => {
    try {
      const r = await fetch(`${root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`);
      if (!r.ok) return [];
      const j = await r.json();
      return ((j.resources && j.resources.results && j.resources.results.products) || []).map((p) => ({
        title: p.title, url: p.url, handle: p.handle, price: Number(p.price), image: p.image || (p.featured_image && p.featured_image.url), variant: null, available: p.available !== false,
      }));
    } catch (e) {
      return [];
    }
  };

  /* ---------- UI ---------- */
  let panel, log, input, chipRow, history = [];
  const save = () => { try { sessionStorage.setItem(KEY, JSON.stringify(history.slice(-40))); } catch (e) {} };

  const build = () => {
    panel = document.createElement('div');
    panel.className = 'rr-chat';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'false');
    panel.setAttribute('aria-label', C.name || 'Reya');
    panel.hidden = true;
    panel.innerHTML = `
      <header class="rr-chat__head">
        <span class="rr-chat__avatar" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M12 2c.6 4.8 2.9 7.2 8 8-5.1.8-7.4 3.2-8 8-.6-4.8-2.9-7.2-8-8 5.1-.8 7.4-3.2 8-8z"/></svg></span>
        <div class="rr-chat__who"><p class="rr-chat__name">${esc(C.name)}</p><p class="rr-chat__status"><span class="rr-chat__dot"></span>${esc(C.status)}</p></div>
        <button type="button" class="rr-chat__icon" data-rr-chat-reset aria-label="${esc(C.resetLabel)}" title="${esc(C.resetLabel)}"><svg viewBox="0 0 24 24"><path d="M4 12a8 8 0 1 0 2.4-5.7"/><path d="M4 4.5v4h4"/></svg></button>
        <button type="button" class="rr-chat__icon" data-rr-chat-close aria-label="${esc(C.closeLabel)}"><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6 6 18"/></svg></button>
      </header>
      <div class="rr-chat__log" role="log" aria-live="polite"></div>
      <div class="rr-chat__chips" data-rr-chat-chips></div>
      <form class="rr-chat__form" novalidate>
        <input class="rr-chat__input" type="text" name="q" autocomplete="off" enterkeyhint="send" placeholder="${esc(C.placeholder)}" aria-label="${esc(C.placeholder)}">
        <button class="rr-chat__send" type="submit" aria-label="${esc(C.sendLabel)}"><svg viewBox="0 0 24 24"><path d="M4 12h15M13 6l6 6-6 6"/></svg></button>
      </form>
      ${C.waUrl ? `<a class="rr-chat__wa" href="${esc(C.waUrl)}?text=${encodeURIComponent(C.waMessage || '')}" target="_blank" rel="noopener"><svg viewBox="0 0 24 24"><path d="M4 20.5l1.2-4.1A8.5 8.5 0 1 1 8.4 19.5z"/></svg>${esc(C.waLabel)}</a>` : ''}`;
    document.body.appendChild(panel);
    log = panel.querySelector('.rr-chat__log');
    input = panel.querySelector('.rr-chat__input');
    chipRow = panel.querySelector('[data-rr-chat-chips]');

    panel.querySelector('[data-rr-chat-close]').addEventListener('click', close);
    panel.querySelector('[data-rr-chat-reset]').addEventListener('click', reset);
    panel.querySelector('form').addEventListener('submit', (e) => {
      e.preventDefault();
      const q = input.value.trim();
      if (!q) return;
      input.value = '';
      ask(q);
    });
    panel.addEventListener('keydown', (e) => e.key === 'Escape' && close());
    panel.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-q]');
      if (chip) return ask(chip.dataset.label || chip.textContent.trim(), chip.dataset.q);
      const add = e.target.closest('[data-rr-chat-add]');
      if (add) addToBag(add);
    });

    try { history = JSON.parse(sessionStorage.getItem(KEY)) || []; } catch (e) { history = []; }
    if (history.length) {
      history.forEach((m) => paint(m.from, m.html, false));
      setChips(chips);
    } else greet();
  };

  const paint = (from, html, store = true) => {
    const row = document.createElement('div');
    row.className = `rr-chat__msg rr-chat__msg--${from}`;
    row.innerHTML = html;
    log.appendChild(row);
    requestAnimationFrame(() => { log.scrollTop = log.scrollHeight; });
    if (store) { history.push({ from, html }); save(); }
    return row;
  };
  const say = (text) => paint('bot', `<div class="rr-chat__bubble">${rich(text)}</div>`);
  const typing = async (ms = 650) => {
    const t = document.createElement('div');
    t.className = 'rr-chat__msg rr-chat__msg--bot';
    t.innerHTML = '<div class="rr-chat__bubble rr-chat__typing"><span></span><span></span><span></span></div>';
    log.appendChild(t);
    log.scrollTop = log.scrollHeight;
    await wait(ms);
    t.remove();
  };
  const setChips = (list) => {
    chipRow.innerHTML = list.map((c) => `<button type="button" class="rr-chat__chip" data-q="${esc(c.q)}" data-label="${esc(c.label)}">${esc(c.label)}</button>`).join('');
  };
  const cards = (products, moreUrl) => {
    const items = products.slice(0, 6).map((p) => `
      <li class="rr-chat__card">
        <a class="rr-chat__card-media" href="${esc(p.url)}">${p.image ? `<img src="${esc(img(p.image, 300))}" alt="${esc(p.title)}" loading="eager" decoding="async" width="150" height="150">` : ''}</a>
        <a class="rr-chat__card-title" href="${esc(p.url)}">${esc(p.title)}</a>
        <span class="rr-chat__card-price">${esc(money(p.price))}</span>
        <span class="rr-chat__card-actions">
          <a class="rr-chat__card-view" href="${esc(p.url)}">${esc(C.viewLabel)}</a>
          ${p.available ? `<button type="button" class="rr-chat__card-add" data-rr-chat-add data-handle="${esc(p.handle)}"${p.variant ? ` data-variant="${p.variant}"` : ''}>${esc(C.addLabel)}</button>` : ''}
        </span>
      </li>`).join('');
    const more = moreUrl ? `<a class="rr-chat__more" href="${esc(moreUrl)}">${esc(C.moreLabel)}</a>` : '';
    return paint('bot', `<ul class="rr-chat__cards" role="list">${items}</ul>${more}`);
  };
  const greet = async () => {
    await typing(500);
    say(C.greeting);
    setChips(chips);
  };
  const reset = () => {
    history = [];
    save();
    log.innerHTML = '';
    greet();
  };

  /* ---------- brain ---------- */
  const matchFaq = (t) => {
    let best = null, score = 0;
    faq.forEach((f) => {
      const s = f.keys.reduce((n, k) => (k && (` ${t} `).includes(` ${k} `) ? n + k.split(' ').length + 1 : (k && t.includes(k) ? n + 1 : n)), 0);
      if (s > score) { score = s; best = f; }
    });
    return score >= 1 ? best : null;
  };
  const matchMeaning = (t) => meanings.find((m) => norm(m.label) === t || m.words.some((w) => w && (` ${t} `).includes(` ${w} `)));
  const budget = (t) => {
    const m = t.match(/(under|below|less than|upto|up to|within|max|budget)\s*(rs|inr|₹)?\s*(\d[\d,]*)/) || t.match(/(\d[\d,]*)\s*(rs|inr|₹)?\s*(budget|or less|and below)/);
    if (!m) return null;
    const n = parseInt((m[3] || m[1]).replace(/,/g, ''), 10);
    return n > 0 ? n : null;
  };

  const ask = async (label, q) => {
    paint('user', `<div class="rr-chat__bubble">${esc(label)}</div>`);
    const t = norm(q || label);
    chipRow.innerHTML = '';

    if (t === 'meaning') {
      await typing();
      say(C.meaningPrompt);
      return setChips(meanings.map((m) => ({ label: m.label, q: norm(m.label) })));
    }
    if (t === 'gift' || /\b(gift|present|surprise)\b/.test(t) && !/wrap|box|packag/.test(t)) {
      await typing();
      say(C.giftPrompt);
      return setChips(gifts.map((g) => ({ label: g.label, q: `giftfor ${g.handle}` })));
    }
    if (t.startsWith('giftfor ')) {
      const handle = t.slice(8).trim().replace(/ /g, '-');
      await typing(800);
      const list = await collectionProducts(handle, 8);
      if (list.length) {
        say(C.found);
        cards(list, `${root}collections/${handle}`);
      } else say(C.fallback);
      return setChips(chips);
    }
    const f = matchFaq(t);
    if (f) {
      await typing();
      say(f.a);
      return setChips(chips);
    }
    const cap = budget(t);
    if (cap) {
      await typing(900);
      const all = await collectionProducts('all', 60);
      const list = all.filter((p) => p.available && p.price <= cap).sort((a, b) => b.price - a.price);
      if (list.length) { say(C.found); cards(list, `${root}collections/all`); } else say(C.fallback);
      return setChips(chips);
    }
    const m = matchMeaning(t);
    if (m) {
      await typing(800);
      if (m.intro) say(m.intro);
      const list = await collectionProducts(m.handle, 8);
      if (list.length) cards(list, `${root}collections/${m.handle}`);
      else say(C.fallback);
      return setChips(chips);
    }
    await typing(800);
    const found = await search(q || label);
    if (found.length) {
      say(C.found);
      cards(found, `${C.searchUrl}?q=${encodeURIComponent(q || label)}&type=product`);
    } else {
      say(C.fallback);
    }
    setChips(chips);
  };

  /* ---------- add to bag (cart drawer aware) ---------- */
  const addToBag = async (btn) => {
    if (btn.disabled) return;
    btn.disabled = true;
    const label = btn.textContent;
    try {
      let id = btn.dataset.variant;
      if (!id) {
        const r = await fetch(`${root}products/${encodeURIComponent(btn.dataset.handle)}.js`);
        const p = await r.json();
        const v = p.variants.find((x) => x.available) || p.variants[0];
        id = v && v.id;
      }
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer && drawer.getSectionsToRender ? drawer.getSectionsToRender().map((s) => s.id) : [];
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }], sections, sections_url: window.location.pathname }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.description || 'error');
      btn.textContent = C.addedLabel;
      btn.classList.add('is-added');
      if (drawer && drawer.renderContents && json.sections) {
        drawer.classList.remove('is-empty');
        close();
        drawer.renderContents(json);
      }
      document.dispatchEvent(new CustomEvent('rr:cart:added'));
    } catch (e) {
      btn.textContent = label;
      btn.disabled = false;
    }
  };

  /* ---------- open / close ---------- */
  const open = () => {
    if (!panel) build();
    panel.hidden = false;
    requestAnimationFrame(() => panel.classList.add('is-open'));
    openBtn.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('rr-chat-open');
    setTimeout(() => input && window.matchMedia('(hover: hover)').matches && input.focus(), 250);
  };
  const close = () => {
    if (!panel || panel.hidden) return;
    panel.classList.remove('is-open');
    openBtn.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('rr-chat-open');
    setTimeout(() => { panel.hidden = true; }, reduce ? 0 : 280);
    openBtn.focus({ preventScroll: true });
  };
  openBtn.addEventListener('click', () => (panel && !panel.hidden ? close() : open()));
})();
